import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { PROFILE, RECHARGES } from '../../routes/app/paths';
import AddContactsModal from './AddContactsModal';

/**
 * Contenido reutilizable de la pantalla de éxito de pago.
 * Sin card wrapper — se puede usar tanto dentro de un <Modal> como
 * envuelto en una card para la página standalone.
 *
 * Props:
 *   onClose – callback al cerrar. Si no se pasa, navega a /profile.
 */
const PaymentSuccessContent = ({ onClose } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pendingContacts, setPendingContacts] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('pending_recharge_contacts') || '[]');
    } catch {
      return [];
    }
  });

  // Solo mostrar números que NO tenían nombre en el momento de la selección.
  // Usamos el campo name del propio pendingContact (capturado antes de la recarga)
  // para evitar depender del estado post-recarga de la API, donde el backend
  // puede haber auto-creado contactos que aún no tienen un nombre real.
  const filteredContacts = useMemo(
    () => pendingContacts.filter((c) => !c.name),
    [pendingContacts],
  );

  const [showAddContacts, setShowAddContacts] = useState(false);
  const [contactsToAdd, setContactsToAdd] = useState([]);

  const handleClose = () => {
    sessionStorage.removeItem('pending_recharge_contacts');
    if (onClose) {
      onClose();
    } else {
      navigate(PROFILE);
    }
  };

  const handleAddSingle = (contact) => {
    setContactsToAdd([contact]);
    setShowAddContacts(true);
  };

  const handleAddAll = () => {
    setContactsToAdd(filteredContacts);
    setShowAddContacts(true);
  };

  const handleModalSuccess = () => {
    setShowAddContacts(false);
    sessionStorage.removeItem('pending_recharge_contacts');
    setPendingContacts([]);
  };

  return (
    <>
      {/* Icon */}
      <div className="mb-5 flex justify-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F5F3EE]">
          <span className="text-4xl">🎉</span>
        </div>
      </div>

      {/* Title */}
      <h1 className="mb-2 text-center text-2xl font-bold text-gray-800">
        {t('payments.success.title')}
      </h1>
      <p className="mb-6 text-center text-sm text-gray-500">
        {t('payments.success.subtitle')}
      </p>

      {/* Contacts list */}
      {filteredContacts.length > 0 && (
        <div className="mb-6 space-y-3">
          <p className="text-sm font-medium text-gray-600">
            ¿Quieres añadir los nuevos contactos a tu Agenda?
          </p>
          {filteredContacts.map((contact, index) => (
            <div
              key={`pending-${contact.phone}-${index}`}
              className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <DevicePhoneMobileIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    +53 {contact.phone}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t('payments.success.newContact')} · {t('payments.success.mobileType')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleAddSingle(contact)}
                className="rounded-full border border-gray-200 px-4 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                {t('payments.success.addContact')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="rounded-full border border-gray-200 px-6 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
        >
          {t('payments.success.close')}
        </button>
        {filteredContacts.length > 0 && (
          <button
            type="button"
            onClick={handleAddAll}
            className="rounded-full bg-[#187672] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#145f5c]"
          >
            {t('payments.success.addAll')}
          </button>
        )}
        {filteredContacts.length === 0 && (
          <Link
            to={RECHARGES}
            className="rounded-full bg-[#187672] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#145f5c]"
          >
            Ver historial
          </Link>
        )}
      </div>

      <AddContactsModal
        contacts={contactsToAdd}
        isOpen={showAddContacts && contactsToAdd.length > 0}
        onClose={() => setShowAddContacts(false)}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export default PaymentSuccessContent;
